import * as z from 'zod'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
export type { Dayjs } from 'dayjs'

import utc from 'dayjs/plugin/utc.js'
import timezone from 'dayjs/plugin/timezone.js'
import isoWeek from 'dayjs/plugin/isoWeek.js'
import relativeTime from 'dayjs/plugin/relativeTime.js'
import isBetween from 'dayjs/plugin/isBetween.js'
import customParseFormat from 'dayjs/plugin/customParseFormat.js'
import objectSupport from 'dayjs/plugin/objectSupport.js'
import LocalizedFormat from 'dayjs/plugin/localizedFormat.js'
import duration from 'dayjs/plugin/duration.js'
import { NumeroOpcionalZod } from './zodComun'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isoWeek)
dayjs.extend(relativeTime)
dayjs.extend(isBetween)
dayjs.extend(customParseFormat)
dayjs.extend(objectSupport)
dayjs.extend(LocalizedFormat)
dayjs.extend(duration)

dayjs.valueOf = function (this: dayjs.Dayjs) {
  return this.toDate().valueOf()
}


declare module 'dayjs' {
    function range(start: Dayjs, end: Dayjs): Dayjs[]
}

dayjs.range = (start: Dayjs, end: Dayjs): Dayjs[] => {
  const range = []
  for (let i = start; i <= end; i = i.add(1, 'day'))
    range.push(i)

  return range
}

export { dayjs }

type PreprocessContext = z.RefinementCtx;

// Preprocesador para convertir varios tipos a Date o undefined
function preprocesadorFecha(v: unknown): Date | undefined { // Añadimos ctx opcional
  // Si el valor es undefined o null, retornamos undefined directamente
  const fx = 'preprocesadorFecha'
  const rutaDelCampo = 'desconocida'
  if (v === undefined || v === null) {
    return undefined;
  }

  const error = new Error('Dayjs componentes resultaron en Date inválido para ${fx}');

  // Si el valor es un objeto Dayjs
  if (dayjs.isDayjs(v)) {
    const internalDate = (v as any).$d;

    if (internalDate instanceof Date && !Number.isNaN(internalDate.getTime())) {
      return new Date(internalDate);
    } else {
      console.warn('${fx}: El objeto $d interno de Dayjs no es un Date válido o no existe.', { path: rutaDelCampo, value: v });
      console.log('${fx}: $d no válido. Intentando extraer componentes de Dayjs.', { path: rutaDelCampo });
      try {
        const year = v.year();
        const month = v.month();
        const day = v.date();
        const hour = v.hour();
        const minute = v.minute();
        const second = v.second();
        const millisecond = v.millisecond();

        const dateFromComponents = new Date(year, month, day, hour, minute, second, millisecond);

        if (!Number.isNaN(dateFromComponents.getTime())) {
          return dateFromComponents;
        } else {
          // --- MODIFICACIÓN AQUÍ: Stack para Dayjs -> componentes -> Date inválido ---
          if (rutaDelCampo) console.warn(
            '${fx}: Creado Date inválido a partir de componentes Dayjs.',
            {
              resultadoDelIntento: dateFromComponents, // Mostrar el objeto Date inválido
              rutaDelCampo,
              stackDeLlamada: error.stack
            }
          );
          // --- Fin de la modificación ---
          return undefined;
        }
      } catch (e) {
        console.error('${fx}: Error durante extracción de componentes de Dayjs:', e, { path: rutaDelCampo });
        return undefined;
      }
    }
  }
  // Si el valor ya es un objeto Date nativo
  else if (v instanceof Date) {
    // Verificamos si es una fecha válida
    if (!Number.isNaN(v.getTime())) {
      return new Date(v);
    } else {
      // --- MODIFICACIÓN AQUÍ: Stack para Input Date nativo inválido ---
      // const error = new Error('Input Date nativo inválido recibido por ${fx}');
      // if (rutaDelCampo) console.warn(
      //     '${fx}: El input Date nativo es inválido.',
      //     {
      //         valorDelInput: v, // Mostrar el objeto Date inválido
      //         rutaDelCampo,
      //         stackDeLlamada: error.stack
      //     }
      // );
      // --- Fin de la modificación ---
      return undefined;
    }
  }
  // Si el valor es un string
  else if (typeof v === 'string') {
    const date = new Date(v);
    if (date instanceof Date && !Number.isNaN(date.getTime())) {
      return new Date(date);
    }
    // Si es un string pero no se puede parsear, mostramos una advertencia
    // --- MODIFICACIÓN AQUÍ: Stack para Input string no parseable ---
    const error = new Error('Input string no parseable a Date recibido por ${fx}');
    console.warn(
      '${fx}, es string pero no logra ser parseado',
      {
        tipoDeInput: typeof v,
        valorDelInput: v,
        rutaDelCampo,
        stackDeLlamada: error.stack
      }
    );
    // --- Fin de la modificación ---
    return undefined;
  }
  // Si el valor no es de ninguno de los tipos esperados
  else {
    // Crear un objeto Error para capturar el stack trace
    const error = new Error('Input de tipo desconocido recibido por ${fx}');

    console.warn(
      '${fx}: El input es de un tipo desconocido.',
      {
        tipoDeInput: typeof v,
        valorDelInput: v,
        rutaDelCampo,
        stackDeLlamada: error.stack
      }
    );
    return undefined;
  }
}


function preprocesadorFechaYMD(v: unknown): string | undefined {
  if (typeof v !== 'string')
    return preprocesadorFecha(v)?.toISOString().split('T')[0]
  const validable = dayjs(v, 'YYYY-MM-DD')
  return validable.isValid() ? v : undefined
}

export const FechaZod = z.preprocess(
  preprocesadorFecha,
  z.date()
);

export const FechaYMDZod = z.preprocess(preprocesadorFechaYMD, z.string())
export const FechaYMDOpcional = z.preprocess(preprocesadorFechaYMD, z.string().optional())

export const FechaOpcionalZod = z.preprocess(v => preprocesadorFecha(v), z.date().optional()).optional()
export const FechaONullZod = z.preprocess(v => preprocesadorFecha(v) || null, z.date().nullable())
export const FechaOAhoraZod = z.preprocess(v => preprocesadorFecha(v) || new Date(), z.date())

function PreprocesadorDayjs(v: unknown) {
  if (dayjs.isDayjs(v))
    return v
  if (typeof v === 'string')
    return dayjs(v)
  if (typeof v === 'object')
    return dayjs(v)
  return null
}

export const FechaADayjsZod = z
  .preprocess(PreprocesadorDayjs, z.custom<Dayjs>())

function preprocesadorFechaDMY(v: unknown): Date | undefined {
  if (v === undefined || v === null)
    return undefined
  if (typeof v !== 'string')
    return undefined
  return dayjs(v, 'DD/MM/YYYY').toDate()
}
export const DMYaFechaZod = z.preprocess(preprocesadorFechaDMY, z.date())
export const FechaDMYAISOZod = DMYaFechaZod.transform(v => dayjs(v).toISOString())

export const FechaADayjsOpcionalZod = z
  .preprocess(v => preprocesadorFecha(v) || new Date(), z.date()).optional()
  .transform(v => v ? dayjs(v) : undefined)

export const FechaAISOZod = z
  .preprocess(v => preprocesadorFecha(v) || new Date(), z.date())
  .transform(v => dayjs(v).toISOString())

export const FechaAYMDZod = z
  .preprocess(v => preprocesadorFecha(v) || new Date(), z.date())
  .transform(v => dayjs(v).format('YYYY-MM-DD'))

export function EsFecha(puedeSer: unknown) {
  const revision = FechaZod.safeParse(puedeSer)
  return revision.success
}

const horaRegex = /^(0\d|1\d|2[0-3]):[0-5]\d$/
export const HoraZod = z.string().trim().regex(horaRegex)
export const DiaSemanaZod = z.number().min(1).max(7)

export const DuracionZod = z.object({
  years: NumeroOpcionalZod,
  months: NumeroOpcionalZod,
  days: NumeroOpcionalZod,
  hours: NumeroOpcionalZod,
  minutes: NumeroOpcionalZod,
  seconds: NumeroOpcionalZod,
  milliseconds: NumeroOpcionalZod
})

export type Duracion = z.infer<typeof DuracionZod>
